import React, { useEffect } from 'react';
import _ from 'lodash';
import store, { brokerClientActions } from '@apply/adminStore';
import { Accordion, SortableHeader, InformModal } from '../../common/SharedComponents';
import { BrokerClientApi, FileApi } from '@apply/api';
import { LoadingModal } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { RefreshEvents } from '../../common/SharedEvents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faArrowCircleDown, faHourglassHalf, faFileExcel } from '@fortawesome/free-solid-svg-icons';

const activeColumns = [
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Broker First Name', label: 'Broker First Name', id: 'firstName' },
    { key: 'Last Name', label: 'Last Name', id: 'lastName' },
    {
        key: 'Point of Contact',
        label: 'Point of Contact',
        id: 'pointOfContact',
    },
    { key: 'B-Client Flgd', label: 'Flag', id: 'clientIsFlagged' },
    { key: 'B-Client (Rep.)', label: 'B-Client (Rep.)', id: 'personName' },
    { key: 'Business', label: 'Business', id: 'businessName' },
    { key: 'Expiration', label: 'Expiration', id: 'contractMonthsRemaining' },
    { key: 'Deals', label: 'Deals', id: 'dealNumber' },
    { key: 'ICCA Co-signed by', label: 'ICCA Co-signed by', id: 'acceptedBy' },
];

const ExpiredBrokerClientTable = () => {
    const searchState = useSelector((state) => state.searchState);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [isLoading, set_isLoading] = React.useState(false);
    const [openCreatePdfModal, set_openCreatePdfModal] = React.useState(false);
    const [createPdfModalText, set_createPdfModalText] = React.useState('');

    const _handleRowClick = (row) => {
        dispatch(brokerClientActions.setBrokerClientId({ brokerClientId: row.id }));
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: true,
            })
        );
    };

    const _refreshGrid = (e) => {
        e.preventDefault();
        _loadBrokerClients();
    };

    const _handleCreatePdfConfirm = () => {
        set_openCreatePdfModal(false);
    };

    const _loadBrokerClients = async () => {
        const response = await brokerClientApi.getExpiredBrokerClientGridRows();

        if (response.statusCode !== 200) return;

        set_rows(
            response.result.map((b) => ({
                ...b,
                expirationText: b.manualExpirationDate
                    ? 'No Deal / Initial Expiry'
                    : `${b.contractMonthsRemaining * -1} months ago`,
            }))
        );
    };

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'name',
        direction: 'asc',
    });

    const matchFirstName = (s) =>
        s.firstName && s.firstName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchLastName = (s) =>
        s.lastName && s.lastName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchPersonName = (s) =>
        s.personName && s.personName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchBusinessName = (s) =>
        s.businessName && s.businessName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter(
            (s) =>
                !searchState.searchText.brokerClient ||
                matchFirstName(s) ||
                matchLastName(s) ||
                matchPersonName(s) ||
                matchBusinessName(s)
        )
        .orderBy(sort.column, sort.direction)
        .value();

    const _downloadIcca = async (iccaDownloadToken) => {
        const response = await fileApi.downloadUrl({
            token: iccaDownloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const _createIcca = async (id) => {
        try {
            set_isLoading(true);
            const response = await brokerClientApi.createBrokerAgreementPdf(id);

            if (response?.statusCode === 200) {
                set_createPdfModalText('The system was able to create the ICCA. The PDF is now available for download.');
                refreshEvents.dispatchRefreshEvent();
            } else {
                set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
            }
        } catch {
            set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
        } finally {
            set_isLoading(false);
        }
    };

    useEffect(() => {
        async function load() {
            await _loadBrokerClients();

            refreshEvents.addRefreshListener(_loadBrokerClients);
        }

        load();
    }, []);

    React.useEffect(() => {
        if (createPdfModalText) set_openCreatePdfModal(true);
    }, [createPdfModalText]);

    return (
        <Accordion title='Expired ICCA'>
            <div className='card'>
                <div className='card-section'>
                    <table>
                        <thead>
                            <tr>
                                {activeColumns.map((col) => (
                                    <SortableHeader
                                        key={col.key}
                                        column={col}
                                        sort={sort}
                                        onPress={set_sort}
                                    />
                                ))}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row) => (
                                <tr key={row.id}>
                                    <td
                                        className='isFlagged'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.isFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='Broker is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='primary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.firstName}
                                    </td>
                                    <td
                                        className='primary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.lastName}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.pointOfContact}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.clientIsFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='B-Client is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.personName}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.businessName}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.expirationText}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.dealNumber}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.acceptedBy}
                                    </td>
                                    <td className='secondary actions'>
                                        {row.iccaDownloadToken && !row.generatingIccaPdfUrl && (
                                            <button
                                                className='gridButton'
                                                onClick={() => _downloadIcca(row.iccaDownloadToken)}
                                            >
                                                <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                                            </button>
                                        )}
                                        {row.generatingIccaPdfUrl && (
                                            <span
                                                style={{
                                                    color: 'black',
                                                    width: '90px',
                                                    textAlign: 'center',
                                                    display: 'inline-block',
                                                }}
                                                onClick={(e) => _refreshGrid(e)}
                                            >
                                                <FontAwesomeIcon
                                                    title='ICCA PDF is generating...'
                                                    icon={faHourglassHalf}
                                                />
                                            </span>
                                        )}
                                        {!row.iccaDownloadToken && !row.generatingIccaPdfUrl && row.canGenerateIccaPdf && (
                                            <button
                                                className='gridButton red-text'
                                                onClick={() => _createIcca(row.id)}
                                            >
                                                <FontAwesomeIcon icon={faFileExcel} /> Create PDF
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <InformModal
                isOpen={openCreatePdfModal}
                confirmCallback={_handleCreatePdfConfirm}
                text={createPdfModalText}
            />
            {isLoading && (
                <LoadingModal
                    open={true}
                    message={'Generating PDF...'}
                />
            )}
        </Accordion>
    );
};

export default ExpiredBrokerClientTable;
